<template>
  <RevCard class="flex-1 p-24">
    <RevTag
      v-if="isMissingLegalDocuments"
      class="mb-8"
      :label="i18n(translations.toDoLabel)"
      variant="warning"
    />
    <h2 class="heading-2 mb-8">
      {{ i18n(translations.title) }}
    </h2>
    <WithSkeletonLoader :is-loading="pending">
      <template v-if="legalDocumentsMetadata && legalDocuments">
        <LegalDocumentsList
          :document-types="legalDocumentsMetadata.legalDocumentTypes"
          :legal-documents
          @delete-legal-document="handleLegalDocumentDelete"
        />
        <LegalDocumentsForm
          :document-types="legalDocumentsMetadata.legalDocumentTypes"
          @legal-document-uploaded="handleLegalDocumentUpload"
        />
      </template>
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevTag } from '@ds/components/Tag'

import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './LegalDocuments.translations'
import LegalDocumentsForm from './components/LegalDocumentsForm/LegalDocumentsForm.vue'
import LegalDocumentsList from './components/LegalDocumentsList/LegalDocumentsList.vue'

defineProps<{
  isMissingLegalDocuments: boolean
}>()

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const [
  {
    data: legalDocumentsMetadata,
    error: legalDocumentsMetadataError,
    pending: legalDocumentsMetadataPending,
  },
  {
    data: legalDocuments,
    error: legalDocumentsError,
    pending: legalDocumentsPending,
    refresh,
  },
] = await Promise.all([
  useHttpLazyFetch(sellerProfileAPI.getLegalDocumentsMetadata),
  useHttpLazyFetch(sellerProfileAPI.getLegalDocuments),
])

const pending = computed(
  () => legalDocumentsMetadataPending.value || legalDocumentsPending.value,
)
const error = computed(
  () => legalDocumentsMetadataError.value || legalDocumentsError.value,
)

if (error.value) {
  openErrorToast()
}

function handleLegalDocumentDelete() {
  refresh()
}

function handleLegalDocumentUpload() {
  refresh()
}
</script>
